/* Gift Sets uses the individual gift selection engine, with its own catalogue and saved state. */
window.GiftSetsFlow = {
  configure(filters, selection) {
    const root = document.querySelector('[data-gift-sets]');
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const config = JSON.parse(root.querySelector('[data-gs-config]').textContent);
    const flow = Object.create(this);
    Object.assign(flow, { root, config, filters, selection, ready: false, category: null, productDetails: new Map() });
    window.giftSetsFlow = flow;
    flow.configureCatalogue();
    flow.configureSelection();
    flow.bind();
    const requested = new URLSearchParams(window.location.search).get('gift_set_category');
    const editing = selection._getEditingHamperData();
    flow.category = requested === 'all' || config.categories.some(category => category.handle === requested)
      ? requested : (editing ? 'all' : null);
    root.dataset.categorySelected = String(!!flow.category);
  },

  error(message) {
    const notice = this.root.querySelector('[data-gs-error]');
    notice.textContent = message || '';
    notice.hidden = !message;
    if (message) notice.scrollIntoView({ block: 'center', behavior: 'smooth' });
  },

  configureCatalogue() {
    const flow = this;
    const filters = this.filters;
    filters.config.FILTER_STATE_KEY = 'giftSetsFilterState';
    filters.config.USE_CACHING = false;
    filters.state.personalised = [];
    this.root.querySelector('.custom-gifts-grid').dataset.collectionHandle = 'gift-sets';

    const loadState = filters.loadFilterState;
    filters.loadFilterState = function () {
      try { loadState.call(this); } catch (error) { localStorage.removeItem(this.config.FILTER_STATE_KEY); }
    };
    filters.loadAllProducts = async function () {
      flow.ready = false;
      flow.root.querySelector('[data-gs-loading]').hidden = false;
      flow.root.querySelector('[data-gs-retry]').hidden = true;
      const handles = [...new Set([
        flow.config.allCollection, flow.config.popularCollection,
        ...flow.config.categories.map(category => category.handle)
      ].filter(Boolean))];
      try {
        if (!handles.length) throw new Error('No gift set collections are configured.');
        const catalogues = await Promise.all(handles.map(async handle => {
          const products = [];
          for (let page = 1; ; page++) {
            const response = await fetch(`${flow.shopRoot()}collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`, { headers: { Accept: 'application/json' } });
            if (!response.ok) throw new Error(`Could not load collection ${handle}.`);
            const data = await response.json();
            if (!Array.isArray(data.products)) throw new Error('Invalid gift set catalogue response.');
            products.push(...data.products);
            if (data.products.length < 250) break;
          }
          return { handle, products };
        }));
        const products = new Map();
        catalogues.forEach(catalogue => catalogue.products.forEach(product => {
          const tags = (Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',')).map(tag => String(tag).trim());
          if (tags.includes('hidden-personalisation-fee') || /^personalisation-fee|^personalized-logo-fee/.test(product.handle || '')) return;
          const existing = products.get(String(product.id));
          if (existing) existing.giftSetCollections.push(catalogue.handle);
          else products.set(String(product.id), Object.assign({}, product, { tags, giftSetCollections: [catalogue.handle] }));
        }));
        this.allProducts = [...products.values()].map(product => Object.assign(this.createProductDataObject(product), { giftSetCollections: product.giftSetCollections }));
        this.filteredProducts = [...this.allProducts];
        this.originalOrder = [...this.allProducts];
        await flow.loadPersonalisationTargets();
        flow.ready = true;
        flow.error(flow.resetSelectionNotice ? 'Please choose one gift set at a time. Your existing basket has not changed.' : '');
        flow.renderPopular();
        // The catalogue arrives after the shared engine restores its saved selection.
        flow.selection._enrichSelectedItemsFromCatalog();
        flow.selection._render();
        if (flow.selection.mode === 'personalise') flow.selection._renderPersonalise();
        if (flow.selection.mode === 'messaging') flow.selection._renderMessaging();
        if (flow.selection.items.some(item => item.personalisationReviewRequired)) await flow.selection.goToMode('personalise');
      } catch (error) {
        this.allProducts = [];
        flow.error(handles.length ? 'We could not load the gift sets. Please try again.' : 'Gift sets will be available soon.');
        flow.root.querySelector('[data-gs-retry]').hidden = !handles.length;
        throw error;
      } finally {
        flow.root.querySelector('[data-gs-loading]').hidden = true;
      }
    };

    const applyFilters = filters.applyFilters;
    filters.applyFilters = function (products) {
      const categoryProducts = products.filter(product => (flow.category === 'all' || !flow.category || product.giftSetCollections.includes(flow.category)) && this.productHasAvailableVariant(product));
      return applyFilters.call(this, categoryProducts);
    };
    const createCard = filters.createProductElement;
    filters.createProductElement = function (product) {
      const card = createCard.call(this, product);
      const variants = (product.variants || []).filter(variant => variant.available);
      if (!variants.length) return card;
      const selectedItem = flow.selection.items.find(item => String(item.id) === String(product.id));
      const initial = variants.find(variant => selectedItem && String(variant.id) === String(selectedItem.variantId)) || [...variants].sort((a, b) => Number(a.price) - Number(b.price))[0];
      card.dataset.variantId = initial.id;
      flow.updateCardPrice(card, initial);
      card.querySelector('[data-tg-toggle]')?.remove();
      card.querySelector('.product-view-info')?.remove();
      if (variants.length > 1) {
        const picker = document.createElement('select');
        picker.className = 'gift-sets__variant';
        picker.dataset.gsVariant = '';
        picker.setAttribute('aria-label', `Choose an option for ${product.title}`);
        variants.forEach(variant => {
          const option = document.createElement('option');
          option.value = variant.id;
          option.textContent = `${variant.title || 'Option'} — ${flow.money(Number(variant.price))}`;
          option.selected = String(variant.id) === String(initial.id);
          picker.appendChild(option);
        });
        card.appendChild(picker);
      }
      const viewButton = document.createElement('button');
      viewButton.type = 'button';
      viewButton.className = 'gift-sets__view-hamper hw-btn hw-btn--dark hw-btn--small';
      viewButton.dataset.gsViewHamper = '';
      viewButton.textContent = 'View Gift';
      viewButton.setAttribute('aria-label', `View ${product.title}`);
      viewButton.setAttribute('aria-haspopup', 'dialog');
      card.appendChild(viewButton);
      card.querySelectorAll('a[href*="/products/"]').forEach(link => link.addEventListener('click', event => {
        event.preventDefault();
        this.openProductModal(this.allProducts.find(item => String(item.id) === String(product.id)), card);
      }));
      return card;
    };
    const openModal = filters.openProductModal;
    filters.openProductModal = function (product, sourceCard = product.element) {
      openModal.call(this, product);
      const variant = product.variants?.find(variant => String(variant.id) === String(sourceCard?.dataset.variantId));
      if (variant) document.getElementById('modal-product-price').textContent = flow.money(Number(variant.price));
      const button = document.getElementById('modal-product-link');
      button.textContent = 'Choose gift set';
      button.href = '#';
      button.onclick = event => {
        event.preventDefault();
        flow.chooseCategory(flow.category || 'all');
        flow.selection.add(product.id, sourceCard?.querySelector('[data-gs-view-hamper]'));
        this.closeProductModal();
      };
    };
  },

  configureSelection() {
    const flow = this;
    const selection = this.selection;
    Object.assign(selection, {
      STORAGE_KEY: 'gs_selected_items_v1', PERSONALISATION_KEY: 'gs_personalisations_v1',
      GIFT_WRAP_KEY: 'gs_gift_wrap_v1', THEMED_CARD_KEY: 'gs_themed_card_v1',
      MODE_KEY: 'gs_mode_v1', MESSAGING_KEY: 'gs_messaging_v1',
      // Match the standard text personalisation price used by Themed Hampers.
      PERSONALISATION_PRICE: 4,
      personalisationVariants: this.config.feeVariants || [],
      showGiftSetError: message => flow.error(message)
    });
    selection.hasSingleGiftSet = function () {
      return this.items.length === 1 && Number(this.items[0].quantity) === 1;
    };
    selection.resetGiftSetDraft = function () {
      this.items = [];
      this.personalisations = {};
      this.messaging = { to: '', from: '', leaveBlank: false, message: '' };
      this.selectedThemedCard = null;
      this.mode = 'select';
      this._persist();
      this._persistPersonalisations();
      this._persistMessaging();
      this._persistThemedCard();
      this._updateSelectedThemedCardWidget();
    };
    selection.recoverGiftSetEdit = async function (editingData, editSession) {
      const response = await fetch(`${flow.shopRoot()}cart.js`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Could not check the edited basket.');
      const cart = await response.json();
      // A lost response can follow a successful update. Keep the replacement in that case.
      const originalsRemain = cart.items.some(item => editingData.itemKeys.includes(item.key));
      if (!originalsRemain) return;
      const updates = {};
      cart.items.filter(item => item.properties?._hamperId === editingData.hamperId && item.properties?._editSession === editSession)
        .forEach(item => { updates[item.key] = 0; });
      const rollback = await fetch(`${flow.shopRoot()}cart/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates })
      });
      if (!rollback.ok) throw new Error('Please check your basket before trying again.');
      throw new Error('The original gift sets have been kept. Please try again.');
    };
    selection._getGiftWrapConfig = () => ({ personalisationVariantId: null, paperProducts: [], individualWrapItems: [] });
    const restore = selection._restore;
    selection._restore = function () {
      restore.call(this);
      if (this.items.length && !this.hasSingleGiftSet()) {
        // Older drafts used one message for several sets. Do not carry that into the new journey.
        if (this._getEditingHamperData()) localStorage.removeItem('editingHamperData');
        this.resetGiftSetDraft();
        flow.resetSelectionNotice = true;
      }
      this.giftWrap = { wrapYourself: false, paperQuantities: {}, addPenSellotape: false, individualWraps: {} };
      if (this.mode === 'gift-wrapping') this.mode = 'messaging';
    };
    const applyMode = selection._applyMode;
    selection._applyMode = function () {
      applyMode.call(this);
      flow.renderStep();
    };
    const goToMode = selection.goToMode;
    selection.goToMode = async function (mode) {
      if (mode === 'gift-wrapping') mode = 'messaging';
      if (mode !== 'select' && (!flow.ready || !this.hasSingleGiftSet())) {
        flow.error(flow.ready ? 'Choose one gift set to continue.' : 'Please wait for your gift sets to load.');
        return;
      }
      if (mode !== 'select') {
        try { await flow.loadPersonalisationTargets(); }
        catch (error) { flow.error('We could not load personalisation options. Please try again.'); return; }
      }
      const reviewRequired = this.items.some(item => item.personalisationReviewRequired);
      if (reviewRequired && mode === 'messaging') mode = 'personalise';
      if (mode === 'messaging' && !flow.validatePersonalisations()) return;
      flow.error('');
      goToMode.call(this, mode);
      if (reviewRequired && mode === 'personalise') {
        flow.error('Please review each item. Your previous set-level personalisation has been kept on the first matching item only.');
        this.items.forEach(item => { delete item.personalisationReviewRequired; });
        this._persist();
      }
    };
    const enrich = selection._enrichSelectedItemsFromCatalog;
    selection._enrichSelectedItemsFromCatalog = function () {
      const result = enrich.call(this);
      this.items.forEach(item => {
        const product = this._findCatalogProductForItem(item);
        const variant = product?.variants.find(variant => String(variant.id) === String(item.variantId));
        if (variant) item.price = Number(variant.price);
        if (item.personalisationTargets?.some(target => this._hasPersonaliseTag(target.tags))) item.canPersonalise = true;
      });
      return result;
    };
    const personalisationTypes = selection._getPersonalisationTypes;
    selection._getPersonalisationTypes = function (tags) {
      const item = this.items.find(item => item.tags === tags);
      const types = item?.personalisationTargets?.length ? [] : personalisationTypes.call(this, tags);
      (item?.personalisationTargets || []).forEach(target => {
        personalisationTypes.call(this, target.tags).forEach(type => types.push(Object.assign({}, type, {
          key: `${target.instanceKey}:${type.key}`,
          legacyKey: `${target.id}:${type.key}`,
          giftSetTarget: target,
          propertyName: `${target.displayTitle} - ${type.propertyName}`,
          summaryLabel: `${target.displayTitle} - ${type.summaryLabel}`
        })));
      });
      return types;
    };
    const add = selection.add;
    selection.add = async function (productId, button) {
      if (!flow.ready || this.choosingGiftSet) return;
      const card = button?.closest('.product-card') || flow.root.querySelector(`.product-card[data-product-id="${productId}"]`);
      const product = flow.filters.allProducts.find(product => String(product.id) === String(productId));
      const variant = product?.variants.find(variant => String(variant.id) === String(card?.dataset.variantId));
      if (!card || !variant?.available) return;
      this.choosingGiftSet = true;
      try {
        if (!this.hasSingleGiftSet() || !this.has(productId)) this.resetGiftSetDraft();
        if (!this.has(productId)) add.call(this, productId, button);
        const item = this.items[0];
        Object.assign(item, { variantId: variant.id, price: Number(variant.price), quantity: 1 });
        this._persist();
        this._render();
        flow.resetSelectionNotice = false;
        if (!flow.category) flow.chooseCategory('all');
        await this.goToMode('personalise');
      } finally {
        this.choosingGiftSet = false;
      }
    };
    const remove = selection.remove;
    selection.remove = function (productId) {
      remove.call(this, productId);
      flow.renderStep();
    };
    // Extra copies are created as separate, editable groups by the basket quantity controls.
    selection.updateQuantity = function () {};
    const submit = selection.submitToCart;
    selection.submitToCart = async function () {
      if (this.submittingGiftSets) return;
      if (!flow.ready) { flow.error('Please wait for your gift sets to load.'); return; }
      if (!this.hasSingleGiftSet()) { flow.error('Choose one gift set to continue. Add extra copies in your basket.'); return; }
      if (this.items.some(item => item.personalisationReviewRequired)) { await this.goToMode('personalise'); return; }
      if (!flow.validatePersonalisations()) return;
      this.submittingGiftSets = true;
      try {
        // Check the current product data before accepting a restored or edited selection.
        for (const item of this.items) {
          if (!flow.filters.allProducts.some(product => String(product.id) === String(item.id))) {
            throw new Error(`${item.title} is no longer in our gift set selection. Please remove it to continue.`);
          }
          const response = await fetch(`${flow.shopRoot()}products/${encodeURIComponent(item.handle)}.js`, { cache: 'no-store' });
          if (!response.ok) throw new Error(`${item.title} is no longer available. Please remove it from your selection.`);
          const product = await response.json();
          const variant = product.variants.find(variant => String(variant.id) === String(item.variantId));
          if (!variant?.available) throw new Error(`${item.title} is no longer available. Please choose another option or remove it.`);
          item.price = Number(variant.price) / 100;
        }
        flow.error('');
        await submit.call(this);
      } catch (error) {
        flow.error(error.message || 'We could not check your gift sets. Please try again.');
      } finally {
        this.submittingGiftSets = false;
      }
    };
  },

  validatePersonalisations() {
    for (const item of this.selection.items) {
      const missing = this.selection._getPersonalisationTypes(item.tags).some(type => {
        const state = this.selection._getPersonalisationState(item.id, type.key);
        return state.enabled && !String(type.isLogo ? state.logoUrl || '' : state.value || '').trim();
      });
      if (missing) {
        this.error(`Please complete the personalisation for ${item.title}, or turn that option off.`);
        return false;
      }
    }
    return true;
  },

  async loadPersonalisationTargets() {
    await Promise.all(this.selection.items.map(async item => {
      if (!item.handle) return;
      let details = this.productDetails.get(item.handle);
      if (!details) {
        // JSON content negotiation bypasses the Liquid view and returns Shopify's ordinary product JSON.
        const response = await fetch(`${this.shopRoot()}products/${encodeURIComponent(item.handle)}?view=gift-set-json`, { headers: { Accept: 'text/html' }, cache: 'no-store' });
        if (!response.ok) throw new Error('Could not load gift set contents.');
        const data = await response.json();
        if (!Array.isArray(data.contents)) throw new Error('Invalid gift set contents response.');
        details = data.contents;
      }
      const contents = new Map();
      details.forEach(target => {
        if (!target?.id || !Array.isArray(target.tags)) throw new Error('Invalid gift set content.');
        const quantity = Number(target.quantity ?? 1);
        if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 50) throw new Error('Invalid gift set content quantity.');
        const key = `${target.id}:${target.variantId || 'default'}`;
        const existing = contents.get(key);
        if (existing) existing.quantity += quantity;
        else contents.set(key, Object.assign({}, target, { quantity }));
      });
      item.personalisationTargets = [...contents.values()].flatMap(target => {
        const override = (this.config.contentQuantities || []).find(setting => setting.giftSetHandle === item.handle && String(setting.productId) === String(target.id));
        const quantity = Number(override?.quantity ?? target.quantity);
        if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 50) throw new Error('Invalid gift set content quantity.');
        const tags = Array.isArray(target.tags) ? [...target.tags] : [];
        // Honour the same free personalisation overrides as Themed Hamper.
        const overrides = {
          'personalise:name0': ['personalise:name', 'personalise:baby', 'personalise:bunnytoy'],
          'personalise:message0': ['personalise:25'],
          'personalise:logo0': ['personalise:logo']
        };
        Object.entries(overrides).forEach(([tag, paidTags]) => {
          if ((item.tags || []).includes(tag) && paidTags.some(paid => tags.includes(paid)) && !tags.includes(tag)) tags.push(tag);
        });
        return Array.from({ length: quantity }, (_, index) => Object.assign({}, target, {
          tags, quantity, copyIndex: index,
          instanceKey: `${target.id}:${target.variantId || 'default'}:${index}`,
          displayTitle: `${target.title}${quantity > 1 ? ` (${index + 1} of ${quantity})` : ''}`
        }));
      });
      this.productDetails.set(item.handle, details);
      if (item.personalisationTargets.some(target => this.selection._hasPersonaliseTag(target.tags))) item.canPersonalise = true;
      // Preserve earlier item-specific saved values on the first copy only, never on every copy.
      const saved = this.selection.personalisations[item.id];
      const savedOptions = saved?.options || (saved?.enabled ? { [saved.type || 'initials']: saved } : null);
      if (savedOptions) {
        let migrated = false;
        this.selection._getPersonalisationTypes(item.tags).forEach(type => {
          if (type.giftSetTarget?.copyIndex !== 0 || savedOptions[type.key]) return;
          const parentKey = type.type;
          const previousKey = savedOptions[type.legacyKey] ? type.legacyKey : parentKey;
          if (!savedOptions[previousKey]) return;
          savedOptions[type.key] = Object.assign({}, savedOptions[previousKey], { key: type.key, type: type.type });
          if (previousKey === parentKey && savedOptions[previousKey].enabled) item.personalisationReviewRequired = true;
          delete savedOptions[previousKey];
          migrated = true;
        });
        if (migrated) {
          this.selection.personalisations[item.id] = { options: savedOptions };
          this.selection._persistPersonalisations();
          this.selection._persist();
        }
      }
    }));
  },

  shopRoot() { return window.Shopify?.routes?.root || '/'; },
  money(value) { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value); },

  updateCardPrice(card, variant) {
    card.dataset.productPrice = variant.price;
    const price = card.querySelector('.product-card-price');
    if (price) price.textContent = this.money(Number(variant.price));
  },

  renderPopular() {
    const products = this.config.popularCollection ? this.filters.allProducts.filter(product => product.available && product.giftSetCollections.includes(this.config.popularCollection)).slice(0, this.config.popularLimit) : [];
    const grid = this.root.querySelector('[data-gs-popular-grid]');
    grid.replaceChildren(...products.map(product => this.filters.createProductElement(product)));
    this.root.querySelector('[data-gs-popular]').hidden = !products.length;
  },

  chooseCategory(handle, updateUrl = true) {
    if (handle && handle !== 'all' && !this.config.categories.some(category => category.handle === handle)) handle = null;
    this.category = handle;
    this.root.dataset.categorySelected = String(!!handle);
    this.selection.goToMode('select');
    this.filters.currentPage = 1;
    if (this.ready) this.filters.filterProducts();
    if (updateUrl) {
      const url = new URL(window.location.href);
      if (handle) url.searchParams.set('gift_set_category', handle);
      else url.searchParams.delete('gift_set_category');
      window.history.pushState({}, '', url);
    }
    this.renderStep();
  },

  renderStep() {
    const select = this.selection.mode === 'select';
    const landing = select && !this.category;
    this.root.querySelector('[data-gs-landing]').hidden = !landing;
    this.root.querySelector('[data-gs-items-heading]').hidden = !select || landing;
    const category = this.config.categories.find(category => category.handle === this.category);
    this.root.querySelector('[data-gs-heading]').textContent = select && category ? category.title : this.config.heading;
    this.root.querySelector('[data-gs-description]').textContent = select && category ? category.description : this.config.description;
    const crumb = this.root.querySelector('[data-gs-category-breadcrumb]');
    crumb.hidden = !category;
    crumb.textContent = category ? `/ ${category.title}` : '';
    this.root.querySelectorAll('[data-gs-step]').forEach(button => {
      const step = button.dataset.gsStep;
      const active = step === (landing ? 'category' : this.selection.mode);
      if (active) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
      button.disabled = ['personalise', 'messaging'].includes(step) && !this.selection.items.length;
    });
  },

  bind() {
    this.root.addEventListener('click', event => {
      const viewButton = event.target.closest('[data-gs-view-hamper]');
      if (viewButton) {
        const card = viewButton.closest('.product-card');
        const product = this.filters.allProducts.find(item => String(item.id) === card.dataset.productId);
        if (product) this.filters.openProductModal(product, card);
      }
      const category = event.target.closest('[data-gs-category]');
      if (category) this.chooseCategory(category.dataset.gsCategory);
      if (event.target.closest('[data-gs-home]')) this.chooseCategory(null);
      const step = event.target.closest('[data-gs-step]');
      if (step) {
        if (step.dataset.gsStep === 'category') this.chooseCategory(null);
        else if (step.dataset.gsStep === 'select') this.chooseCategory(this.category || 'all');
        else this.selection.goToMode(step.dataset.gsStep);
      }
      if (event.target.closest('[data-gs-retry]')) this.filters.init();
    });
    this.root.addEventListener('change', event => {
      if (!event.target.matches('[data-gs-variant]')) return;
      const card = event.target.closest('.product-card');
      const product = this.filters.allProducts.find(product => String(product.id) === card.dataset.productId);
      const variant = product.variants.find(variant => String(variant.id) === event.target.value);
      card.dataset.variantId = variant.id;
      this.updateCardPrice(card, variant);
      const selected = this.selection.items.find(item => String(item.id) === card.dataset.productId);
      if (selected) {
        Object.assign(selected, { variantId: variant.id, price: Number(variant.price) });
        this.selection._persist();
        this.selection._render();
      }
    });
    window.addEventListener('popstate', () => this.chooseCategory(new URLSearchParams(window.location.search).get('gift_set_category'), false));
  }
};

// The editor replaces section HTML without rerunning this journey's inline engine.
// Refresh only the editor preview so changed collection/settings data is rendered afresh.
if (window.Shopify?.designMode && !window.giftSetsEditorEventsBound) {
  window.giftSetsEditorEventsBound = true;
  document.addEventListener('shopify:section:load', event => {
    if (event.target.querySelector('[data-gift-sets]')) window.location.reload();
  });
}
